/**
 * PublicScenarioModal.js — Browse and search public scenarios.
 *
 * Props:
 * - onSelect:   (scenario) => void   — called with the full scenario data when user picks one
 * - onClose:    () => void
 *
 * Premium-only feature. Uses scenariosAPI.searchPublic() for paginated search.
 * Fetches the full scenario via scenariosAPI.get() on selection.
 */

import { useState, useCallback, useEffect } from 'react';
import { scenariosAPI } from './api';
import { useI18n } from './i18n';

export default function PublicScenarioModal({ onSelect, onClose, openMode = false }) {
  const { t, lang } = useI18n();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(null);

  const loadPage = useCallback(async (searchQ, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const data = openMode
        ? await scenariosAPI.searchOpen(searchQ, pageNum)
        : await scenariosAPI.searchPublic(searchQ, pageNum);
      setResults(data.results || []);
      setPagination(data.pagination || null);
    } catch (err) {
      setError(err.message || t('pub.loadListFailed'));
      setResults([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [openMode, t]);

  // Load first page on mount
  useEffect(() => {
    loadPage('', 1);
  }, [loadPage]);

  const handleSearch = () => {
    setPage(1);
    loadPage(q, 1);
  };

  const handleClear = () => {
    setQ('');
    setPage(1);
    loadPage('', 1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    loadPage(q, newPage);
  };

  const handleSelect = async (scenarioId) => {
    setLoading(true);
    setError(null);
    try {
      const fullScenario = openMode
        ? await scenariosAPI.getOpen(scenarioId)
        : await scenariosAPI.get(scenarioId);
      onSelect(fullScenario);
    } catch (err) {
      setError(err.message || t('pub.loadOneFailed'));
    } finally {
      setLoading(false);
    }
  };

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.page || 1;

  // Page number buttons (max 5 around current)
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, startPage + 4);
  const pageButtons = [];
  for (let p = startPage; p <= endPage; p++) pageButtons.push(p);

  const backdropStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
  };

  const modalStyle = {
    background: '#1e293b', border: '1px solid #334155', borderRadius: '12px',
    width: '100%', maxWidth: '700px',
    // dvh-backed var: the visible viewport, so the modal never lands under
    // the mobile browser chrome in landscape
    maxHeight: 'calc(var(--kv-viewport-h) * 0.85)',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  const inputStyle = {
    flex: 1, padding: '0.5rem 0.75rem', background: '#0f172a',
    border: '1px solid #475569', borderRadius: '6px', color: '#e2e8f0',
    fontSize: '0.9rem', minWidth: 0,
  };

  const btnStyle = {
    padding: '0.5rem 1rem', borderRadius: '6px', cursor: 'pointer',
    fontSize: '0.85rem', fontWeight: 600, border: '1px solid #475569',
    background: '#1e293b', color: '#e2e8f0', whiteSpace: 'nowrap',
  };

  return (
    <div style={backdropStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.25rem', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#e2e8f0' }}>{t(openMode ? 'pub.titleOpen' : 'pub.titlePublic')}</h3>
          <button type="button" onClick={onClose} aria-label={t('fb.close')} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.5rem', cursor: 'pointer', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>

        {/* Search bar */}
        <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #334155', flexShrink: 0 }}>
          <div className="kv-scenario-search" style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('pub.searchPlaceholder')}
              style={inputStyle}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              autoFocus
            />
            <button type="button" style={{ ...btnStyle, background: '#2563eb', color: '#fff', borderColor: '#2563eb' }} onClick={handleSearch}>{t('pub.search')}</button>
            <button type="button" style={btnStyle} onClick={handleClear}>{t('pub.clear')}</button>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '0.75rem 1.25rem' }}>
          {loading && <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '1rem 0', textAlign: 'center' }}>{t('pub.loading')}</div>}
          {error && <div style={{ color: '#ef4444', fontSize: '0.85rem', padding: '1rem 0', textAlign: 'center' }}>{error}</div>}
          {!loading && !error && results.length === 0 && (
            <div style={{ color: '#64748b', fontSize: '0.85rem', padding: '2rem 0', textAlign: 'center' }}>{t('pub.none')}</div>
          )}
          {!loading && !error && results.map((s) => {
            const meta = [];
            // V2 metadata from the backend: movement keys + timeline length
            const mk = s.movementKeys ?? 0;
            const tl = s.timelineDuration ?? 60;
            meta.push(mk > 0 ? t('pub.movementKeys', { n: mk }) : t('pub.emptyTimeline'));
            meta.push(`${tl}s`);
            // The date follows the interface language, not a hard-wed en-US:
            // "Oct 12, 2025" in a Portuguese list of scenarios is a stray.
            const dateStr = s.createdAt ? new Date(s.createdAt).toLocaleDateString(lang, { year: 'numeric', month: 'short', day: 'numeric' }) : '';
            return (
              <div key={s._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.6rem 0.75rem', background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', marginBottom: '0.5rem' }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.15rem' }}>
                    {s.ownerName} · {meta.join(' · ')}{dateStr ? ` · ${dateStr}` : ''}
                  </div>
                </div>
                <button
                  type="button"
                  style={{ ...btnStyle, background: '#16a34a', color: '#fff', borderColor: '#16a34a', flexShrink: 0 }}
                  onClick={() => handleSelect(s._id)}
                  disabled={loading}
                >
                  {t('pub.load')}
                </button>
              </div>
            );
          })}
        </div>

        {/* Info + pagination */}
        {!loading && !error && pagination && pagination.total > 0 && (
          <div style={{ padding: '0.5rem 1.25rem 0.75rem', borderTop: '1px solid #334155', flexShrink: 0 }}>
            <div style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center', marginBottom: '0.5rem' }}>
              {pagination.total === 1
                ? t('pub.showingOne', { from: (currentPage - 1) * pagination.perPage + 1, to: Math.min(currentPage * pagination.perPage, pagination.total) })
                : t('pub.showing', { from: (currentPage - 1) * pagination.perPage + 1, to: Math.min(currentPage * pagination.perPage, pagination.total), total: pagination.total })}
            </div>
            <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {currentPage > 1 && (
                <button type="button" style={btnStyle} onClick={() => handlePageChange(currentPage - 1)}>{t('pub.prev')}</button>
              )}
              {startPage > 1 && (
                <>
                  <button type="button" style={btnStyle} onClick={() => handlePageChange(1)}>1</button>
                  {startPage > 2 && <span style={{ color: '#64748b', padding: '0 0.25rem', display: 'flex', alignItems: 'center' }}>…</span>}
                </>
              )}
              {pageButtons.map((p) => (
                <button
                  key={p}
                  type="button"
                  style={p === currentPage ? { ...btnStyle, background: '#2563eb', color: '#fff', borderColor: '#2563eb' } : btnStyle}
                  onClick={() => handlePageChange(p)}
                  disabled={p === currentPage}
                >
                  {p}
                </button>
              ))}
              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && <span style={{ color: '#64748b', padding: '0 0.25rem', display: 'flex', alignItems: 'center' }}>…</span>}
                  <button type="button" style={btnStyle} onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                </>
              )}
              {currentPage < totalPages && (
                <button type="button" style={btnStyle} onClick={() => handlePageChange(currentPage + 1)}>{t('pub.next')}</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
